import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { AnimatePresence } from 'framer-motion';
import { motion } from 'framer-motion';
import { memo, useState } from 'react';
import { PromptAgentSelectContainer } from './PromptAgentSelectContainer';
import { IconButton } from '~/components/ui/IconButton';
import { classNames } from '~/utils/classNames';

export const PromptActionsButton = memo(
  (props: {
    chatStarted?: boolean;
    actions: {
      active?: boolean;
      icon: string;
      label: string;
      onClick: () => void;
      disabled?: boolean;
      hidden?: boolean;
    }[];
  }) => {
    const { actions, chatStarted } = props;
    const [open, setOpen] = useState(false);

    const visibleActions = actions.filter((item) => !item.hidden);

    return (
      <DropdownMenu.Root modal={false} open={open} onOpenChange={setOpen}>
        <DropdownMenu.Trigger asChild>
          <IconButton
            aria-label="Prompt actions"
            icon="i-heroicons:plus"
            size="sm"
            className="rounded-full [&[data-state=open]_i]:rotate-45 [&[data-state=open]_i]:animate-motion-blur"
            iconClassName="transition-transform duration-300 ease-out"
          />
        </DropdownMenu.Trigger>

        <AnimatePresence>
          {open && (
            <DropdownMenu.Portal forceMount>
              <DropdownMenu.Content asChild sideOffset={5} align="start" side="top">
                <motion.div
                  className="origin-[0_100%] z-10"
                  initial={{ opacity: 0, y: 4, filter: 'blur(2px)' }}
                  animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, y: 4, scale: 0.9, filter: 'blur(2px)' }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                >
                  <div className="grid gap-px rounded-md p-0.5 shadow-sm bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2 border-bolt-elements-borderColor border">
                    {visibleActions.map((item) => (
                      <DropdownMenu.Item
                        key={item.label}
                        disabled={item.disabled}
                        className={`
                          group relative flex h-8 select-none items-center pl-8.5 pr-6 text-sm flex rounded outline-none
                          ${item.active ? 'bg-bolt-elements-item-backgroundAccent text-bolt-elements-item-contentAccent hover:text-bolt-elements-item-contentAccent' : ''}
                          data-[disabled]:pointer-events-none
                          data-[disabled]:text-bolt-elements-textTertiary
                          data-[highlighted]:bg-bolt-elements-item-backgroundActive
                          data-[highlighted]:text-bolt-elements-item-contentActive
                        `}
                        onClick={item.onClick}
                      >
                        <div className="absolute left-2.5 inline-flex size-4 items-center justify-center">
                          <span
                            className={classNames(
                              item.icon,
                              'size-4',
                              item.active ? 'text-bolt-elements-item-contentAccent' : 'text-bolt-ds-iconSecondary',
                            )}
                          />
                        </div>

                        <span>{item.label}</span>
                      </DropdownMenu.Item>
                    ))}
                    {chatStarted && <PromptAgentSelectContainer />}
                  </div>
                </motion.div>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          )}
        </AnimatePresence>
      </DropdownMenu.Root>
    );
  },
);
